import { formatDate } from "@arelia-utils/helpers/dates";
import { formatCurrency } from "@arelia-utils/helpers/formatCurrency";
import { SxProps, TextField, Theme } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { parse } from "date-fns";
import { DataGridState } from "./api";

export type ColumnDefTypes = "text" | "date" | "currency";
export type ColumnDefParams = { row: any, column: ColumnDef };
export type ValueColumnDefParams = { value: any } & ColumnDefParams;
export type EditorColumnDefParams = { setValue: (val: any) => void } & ValueColumnDefParams;

export interface Props<T = any> {
  rows: T[];
  columns: ColumnDef[];
  columnDefaults?: Partial<ColumnDef>;
  sx?: SxProps<Theme>;
  rowHeight?: string;
  getRowStyle?: (params: { row: T }) => SxProps<Theme>;
  isRowEditable?: (params: { row: T }) => boolean;
  onRowUpdate?: (params: { row: T }) => void;
  state?: DataGridState;
  onStateChange?: (state: DataGridState) => void;
  pageSize?: number;
  rowsPerPage?: number[];
  onRowClick?: (row: T) => void;
}

export interface ColumnDef {
  field?: string;
  alternative?: string;
  headerName: string | React.ReactNode;
  type?: ColumnDefTypes
  width?: string;
  editable?: boolean;
  formatter?: (params: ValueColumnDefParams) => any;
  renderer?: (params: ValueColumnDefParams) => JSX.Element;
  renderEditor?: (params: EditorColumnDefParams) => JSX.Element;
  align?: "left" | "center" | "right";
}

export const defaultColumnDef: Partial<ColumnDef> = {
  renderEditor: renderTextEditor
}

export const typedColumnDefs: Record<ColumnDefTypes, Partial<ColumnDef>> = {
  date: {
    formatter: ({ value }) => value ? formatDate(value, 'MM/dd/yyyy') : value,
    renderEditor: ({ value, setValue }) => (<DesktopDatePicker
      value={parse(value, 'yyyy-MM-dd', new Date())}
      onChange={val => setValue(val && formatDate(val, 'yyyy-MM-dd', false))}
      maxDate={new Date()}
      autoFocus={true}
      sx={{
        width: '100%',
        margin: '2px',
        '& .MuiInputBase-input': {
          paddingY: '8px'
        }
      }}
    />)
  },
  currency: {
    formatter: ({ value }) => value ? formatCurrency(value) : value,
    renderEditor: p => renderTextEditor(p, "number")
  },
  text: {
    ...defaultColumnDef
  }
}

function renderTextEditor({ value, setValue }: EditorColumnDefParams, type: string = "text") {
  return <TextField
    sx={{ width: "100%", margin: '4px' }}
    size="small"
    type={type}
    required
    autoFocus={true}
    defaultValue={value}
    onChange={ev => setValue(ev.target.value)}
  />
};